import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Book, Code, Zap, Database, Bot, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";

const docSections = [
  {
    icon: Book,
    title: "Getting Started",
    description: "Learn the basics of Strata in under 10 minutes",
    articles: ["Quick Start Guide", "Creating Your First Base", "Understanding Tables & Views"],
  },
  {
    icon: Database,
    title: "Data Management",
    description: "Import, organize, and manage your data",
    articles: ["CSV Import & Schema Detection", "Field Types Reference", "Linking Records"],
  },
  {
    icon: Zap,
    title: "Automations",
    description: "Build powerful workflows without code",
    articles: ["Automation Basics", "Triggers & Actions", "Natural Language Rules"],
  },
  {
    icon: Bot,
    title: "AI Features",
    description: "Leverage AI to work smarter",
    articles: ["Schema Detection", "Smart Suggestions", "Agent Permissions"],
  },
  {
    icon: Code,
    title: "Integrations",
    description: "Connect Strata to your tools",
    articles: ["n8n Integration", "API Reference", "Webhooks"],
  },
  {
    icon: FileText,
    title: "Forms",
    description: "Collect data from anyone",
    articles: ["Creating Forms", "Form Sharing", "WhatsApp Integration"],
  },
];

const Docs = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 py-16 md:py-24">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-xs font-mono text-muted-foreground mb-4 block">[DOCS]</span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">Documentation</h1>
            <p className="text-lg text-muted-foreground">
              Everything you need to build with Strata
            </p>
          </div>
          
          {/* Quick Start CTA */}
          <div className="max-w-2xl mx-auto mb-16 p-6 rounded-xl border border-border bg-card">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1 text-center sm:text-left">
                <h2 className="text-lg font-semibold mb-1">New to Strata?</h2>
                <p className="text-muted-foreground text-sm">
                  Get up and running in 10 minutes with our quick start guide.
                </p>
              </div>
              <Button 
                className="group shrink-0"
                onClick={() => navigate("/auth")}
              >
                Start Tutorial
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          </div>
          
          {/* Documentation Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {docSections.map((section, idx) => (
              <div 
                key={idx}
                className="p-6 rounded-xl border border-border bg-card hover-lift cursor-pointer group"
              >
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <section.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{section.title}</h3>
                <p className="text-sm text-muted-foreground mb-4">{section.description}</p>
                <ul className="space-y-2">
                  {section.articles.map((article, aIdx) => (
                    <li key={aIdx} className="text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                      → {article}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          {/* Help Section */}
          <div className="max-w-2xl mx-auto mt-16 text-center">
            <p className="text-muted-foreground">
              Can't find what you're looking for?{" "}
              <a href="mailto:support@strata.app" className="text-primary hover:underline">
                Contact support
              </a>
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Docs;
